import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {CalculationStatus} from "../../../enums/calculation-status.enum";
import {InspectionService} from '../services/inspection.service';
import {interval, Subject, Subscription} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {InspectionModel} from "../../../models/inspection.model";

@Component({
    selector: 'app-inspection-item',
    templateUrl: './inspection-item.component.html',
    styleUrls: ['./inspection-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionItemComponent implements OnInit, OnDestroy {
    @Input() inspection!: InspectionModel;
    @Input() index!: number;
    @Output() edit = new EventEmitter<InspectionModel>();
    private destroy$ = new Subject<void>();
    private pollingSubscription?: Subscription;

    constructor(
        private inspectionService: InspectionService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.setupPollingIfNeeded();
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupPollingIfNeeded(): void {
        // Si l'inspection est en cours de calcul, mettre en place un polling
        if (this.needsPolling()) {
            this.startPolling();
        }
    }

    private needsPolling(): boolean {
        const status = this.inspection?.result?.calculationStatus;
        return [
            CalculationStatus.CALCULATING,
            CalculationStatus.QUEUED,
            CalculationStatus.PENDING
        ].includes(status);
    }

    private startPolling(): void {
        // Arrêter tout polling existant
        this.stopPolling();

        // Déterminer l'intervalle en fonction du statut
        const pollingInterval = this.getPollingInterval();

        // Démarrer le polling
        this.pollingSubscription = interval(pollingInterval)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.inspectionService.getInspectionById(this.inspection.id))
            )
            .subscribe({
                next: (response) => {
                    if (response && response.data) {
                        this.inspection = response.data;
                        this.cdr.markForCheck(); // Forcer la détection de changement

                        // Si le calcul est terminé ou a échoué, arrêter le polling
                        if (!this.needsPolling()) {
                            this.stopPolling();
                        }
                    }
                },
                error: (err) => {
                    console.error('Erreur lors du rafraîchissement de l\'inspection', err);
                    // En cas d'erreur, on pourrait réduire la fréquence de polling ou arrêter
                }
            });
    }

    private stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = undefined;
        }
    }

    private getPollingInterval(): number {
        const status = this.inspection?.result?.calculationStatus;

        switch (status) {
            case CalculationStatus.CALCULATING:
                return 5000; // 5 secondes pour les calculs actifs
            case CalculationStatus.QUEUED:
                return 10000; // 10 secondes pour les éléments en file d'attente
            case CalculationStatus.PENDING:
                return 15000; // 15 secondes pour les éléments en attente
            default:
                return 10000; // Valeur par défaut
        }
    }

    // Méthodes existantes pour l'UI (affichage des recommandations, etc.)
    isCalculationCompleted(): boolean {
        return this.inspection?.result?.calculationStatus === CalculationStatus.COMPLETED;
    }

    isCalculationFailed(): boolean {
        return this.inspection?.result?.calculationStatus === CalculationStatus.FAILED;
    }

    onRefreshCalculation(): void {
        if (this.inspection && this.inspection.id) {
            this.inspectionService.forceRecalculate(this.inspection.id)
                .subscribe({
                    next: () => {
                        // Après avoir forcé le recalcul, commencer le polling
                        this.setupPollingIfNeeded();
                    },
                    error: (err: any) => console.error('Erreur lors du forçage du recalcul', err)
                });
        }
    }

    /**
     * Returns the CSS class for the recommendation badge based on its severity
     */
    getRecommendationClass(): string {
        if (!this.inspection.result || !this.inspection.result.ffsRecommendation) {
            return 'normal';
        }

        switch (this.inspection.result.ffsRecommendationCode) {
            case 1:
                return 'do-rbi';
            case 2:
                return 'consider-inspection'
            case 3:
                return 'further-inspection'
            case 4:
                return 'full-inspection';
            default:
                return 'do-rbi';
        }
    }

    isCompleted(): boolean {
        return this.inspection.result?.calculationStatus === CalculationStatus.COMPLETED;
    }

    onEditInspection() {
        this.edit.emit(this.inspection);
    }
}


